import React, { useEffect, useRef, useState } from 'react';
import Button from 'components/crm/ui/Button';
import Modal from 'components/crm/ui/Modal';
import {
  FREQUENCIES,
  MAX_COUNT,
  MAX_INTERVAL,
  MAX_UNTIL_YEARS,
  WEEKDAYS,
  addYears,
  describeRecurrence,
  languageCode,
  nthOf,
  normalizeRule,
  presetsFor,
  sameRule,
  weekdayName,
  weekdayOf,
} from 'helper/recurrence';
import { fromIsoDay } from 'helper/timeLabels';
import useText from 'hooks/useText';

const CUSTOM = 'custom';
const CURRENT = 'current';
const DEFAULT_COUNT = '13';

const selectClass =
  'rounded-lg border border-lineStrong bg-white p-3 text-sm font-medium text-ink outline-none focus:border-primary';
const smallClass =
  'rounded-lg border border-lineStrong bg-white px-2 py-2 text-sm font-medium text-ink outline-none focus:border-primary disabled:opacity-50';

const presetKeyOf = (value, startDay) =>
  presetsFor(startDay).find((preset) => sameRule(preset.rule, value))?.key ??
  null;

// The same date a month later (the 31st falls to the next month's start).
const nextMonthOf = (day) => {
  const date = fromIsoDay(day);
  date.setMonth(date.getMonth() + 1);
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// The draft of the custom form, from the rule shown (or a weekly one on the
// first day, as Google Calendar opens it).
const draftOf = (value, startDay) => ({
  freq: value?.freq ?? 'weekly',
  interval: String(value?.interval ?? 1),
  byWeekday:
    value?.freq === 'weekly' && value.byWeekday?.length
      ? value.byWeekday
      : [weekdayOf(startDay)],
  monthlyBy: value?.monthlyBy ?? 'monthday',
  endType: value?.end?.type ?? 'never',
  until: value?.end?.type === 'until' ? value.end.day : nextMonthOf(startDay),
  count: String(value?.end?.type === 'count' ? value.end.count : DEFAULT_COUNT),
});

const inRange = (raw, max) =>
  /^\d+$/.test(raw) && Number(raw) >= 1 && Number(raw) <= max;

// Mounted while open only: each opening starts from the rule shown.
const CustomRecurrence = ({ value, startDay, onCancel, onDone }) => {
  const { text, plural, language } = useText('recurrence');
  const lang = languageCode(language);
  const [draft, setDraft] = useState(() => draftOf(value, startDay));
  // The Modal closes on Échap; marked handled first, so that the Drawer of
  // the form around it stays open.
  useEffect(() => {
    const onKeyDown = (event) =>
      event.key === 'Escape' && event.preventDefault();
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);
  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const intervalOk = inRange(draft.interval, MAX_INTERVAL);
  const countOk = draft.endType !== 'count' || inRange(draft.count, MAX_COUNT);
  const lastUntil = addYears(startDay, MAX_UNTIL_YEARS);
  const untilOk =
    draft.endType !== 'until' ||
    (Boolean(draft.until) &&
      draft.until >= startDay &&
      draft.until <= lastUntil);
  const valid = intervalOk && countOk && untilOk;
  const interval = intervalOk ? Number(draft.interval) : 1;

  const toggleDay = (code) => {
    const chosen = draft.byWeekday.includes(code);
    // A weekly rule keeps one day at least.
    if (chosen && draft.byWeekday.length === 1) return;
    update({
      byWeekday: chosen
        ? draft.byWeekday.filter((day) => day !== code)
        : [...draft.byWeekday, code],
    });
  };

  const done = () => {
    if (!valid) return;
    const end =
      draft.endType === 'until'
        ? { type: 'until', day: draft.until }
        : draft.endType === 'count'
          ? { type: 'count', count: Number(draft.count) }
          : { type: 'never' };
    onDone(
      normalizeRule(
        {
          freq: draft.freq,
          interval: Number(draft.interval),
          byWeekday: draft.byWeekday,
          monthlyBy: draft.monthlyBy,
          end,
        },
        startDay,
      ),
    );
  };

  const nth = text(`nth_${nthOf(startDay)}`);
  const startName = weekdayName(weekdayOf(startDay), lang);

  return (
    <Modal
      open
      onClose={onCancel}
      title={text('customTitle')}
      width={420}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {text('cancel')}
          </Button>
          <Button disabled={!valid} onClick={done}>
            {text('done')}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm text-body">
        <span>{text('repeatEvery')}</span>
        <input
          type="number"
          min="1"
          max={MAX_INTERVAL}
          inputMode="numeric"
          aria-label={text('intervalLabel')}
          aria-invalid={!intervalOk}
          value={draft.interval}
          onChange={(event) =>
            update({ interval: event.target.value.replace(/[^\d]/g, '') })
          }
          className={`w-20 ${smallClass} ${intervalOk ? '' : 'border-danger'}`}
        />
        <select
          aria-label={text('unitLabel')}
          value={draft.freq}
          onChange={(event) => update({ freq: event.target.value })}
          className={smallClass}
        >
          {FREQUENCIES.map((freq) => (
            <option key={freq} value={freq}>
              {plural(`unit_${freq}`, interval)}
            </option>
          ))}
        </select>
      </div>
      {!intervalOk && (
        <p className="text-xs text-danger">{text('intervalOutOfRange')}</p>
      )}
      {draft.freq === 'weekly' && (
        <div
          role="group"
          aria-label={text('repeatOn')}
          className="flex flex-col gap-2"
        >
          <span className="text-sm text-body">{text('repeatOn')}</span>
          <div className="flex gap-1.5">
            {WEEKDAYS.map((code) => {
              const chosen = draft.byWeekday.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  aria-pressed={chosen}
                  aria-label={weekdayName(code, lang)}
                  onClick={() => toggleDay(code)}
                  className={`h-8 w-8 rounded-full text-xs font-semibold ${
                    chosen
                      ? 'bg-primary text-white'
                      : 'bg-surfaceMuted text-body'
                  }`}
                >
                  {text(`dayLetter_${code}`)}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {draft.freq === 'monthly' && (
        <select
          aria-label={text('monthlyByLabel')}
          value={draft.monthlyBy}
          onChange={(event) => update({ monthlyBy: event.target.value })}
          className={selectClass}
        >
          <option value="monthday">
            {text('monthDayChoice', { day: fromIsoDay(startDay).getDate() })}
          </option>
          <option value="weekday">
            {text('nthWeekdayChoice', { nth, day: startName })}
          </option>
        </select>
      )}
      <fieldset className="flex flex-col gap-2 text-sm text-body">
        <legend className="mb-2">{text('endsLabel')}</legend>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="recurrence-end"
            checked={draft.endType === 'never'}
            onChange={() => update({ endType: 'never' })}
          />
          {text('endNever')}
        </label>
        <div className="flex items-center gap-2">
          <label className="flex w-20 items-center gap-2">
            <input
              type="radio"
              name="recurrence-end"
              checked={draft.endType === 'until'}
              onChange={() => update({ endType: 'until' })}
            />
            {text('endUntil')}
          </label>
          <input
            type="date"
            aria-label={text('endUntilDay')}
            aria-invalid={!untilOk}
            min={startDay}
            max={lastUntil}
            disabled={draft.endType !== 'until'}
            value={draft.until}
            onChange={(event) => update({ until: event.target.value })}
            className={smallClass}
          />
        </div>
        {!untilOk && (
          <p className="text-xs text-danger">{text('untilOutOfRange')}</p>
        )}
        <div className="flex items-center gap-2">
          <label className="flex w-20 items-center gap-2">
            <input
              type="radio"
              name="recurrence-end"
              checked={draft.endType === 'count'}
              onChange={() => update({ endType: 'count' })}
            />
            {text('endCount')}
          </label>
          <input
            type="number"
            min="1"
            max={MAX_COUNT}
            inputMode="numeric"
            aria-label={text('endCountAmount')}
            aria-invalid={!countOk}
            disabled={draft.endType !== 'count'}
            value={draft.count}
            onChange={(event) =>
              update({ count: event.target.value.replace(/[^\d]/g, '') })
            }
            className={`w-20 ${smallClass}`}
          />
          <span>
            {plural(
              'occurrences',
              inRange(draft.count, MAX_COUNT) ? Number(draft.count) : 1,
            )}
          </span>
        </div>
        {!countOk && (
          <p className="text-xs text-danger">{text('countOutOfRange')}</p>
        )}
      </fieldset>
    </Modal>
  );
};

/**
 * « Répéter » of a new appointment, as Google Calendar offers it: `value` is
 * the rule (helper/recurrence.js) or null, `startDay` the first day
 * (YYYY-MM-DD), which names the choices. « Personnaliser… » opens the custom
 * form; a rule that is not one of the choices reads in words. When the first
 * day moves, a chosen preset follows it (« toutes les semaines le lundi »
 * becomes « le mardi »), a custom rule stays.
 */
const RecurrenceField = ({
  id = 'appointment-recurrence',
  value,
  startDay,
  onChange,
}) => {
  const { text, language } = useText('recurrence');
  const lang = languageCode(language);
  const [customOpen, setCustomOpen] = useState(false);
  const day = startDay || null;

  const previousDay = useRef(day);
  useEffect(() => {
    const before = previousDay.current;
    previousDay.current = day;
    if (!before || !day || before === day || !value) return;
    const key = presetKeyOf(value, before);
    if (!key) return;
    const next = presetsFor(day).find((preset) => preset.key === key).rule;
    if (!sameRule(next, value)) onChange(next);
  }, [day, value, onChange]);

  if (!day) return null;
  const presets = presetsFor(day, lang);
  const presetKey = value ? presetKeyOf(value, day) : 'none';

  const pick = (key) => {
    if (key === CUSTOM) return setCustomOpen(true);
    if (key === CURRENT) return undefined;
    return onChange(presets.find((preset) => preset.key === key).rule);
  };

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium leading-5 text-body">
        {text('label')}
      </label>
      <select
        id={id}
        value={presetKey ?? CURRENT}
        onChange={(event) => pick(event.target.value)}
        className={selectClass}
      >
        {presets.map((preset) => (
          <option key={preset.key} value={preset.key}>
            {preset.label}
          </option>
        ))}
        {!presetKey && (
          <option value={CURRENT}>
            {describeRecurrence(value, day, lang)}
          </option>
        )}
        <option value={CUSTOM}>{text('custom')}</option>
      </select>
      {customOpen && (
        <CustomRecurrence
          value={value}
          startDay={day}
          onCancel={() => setCustomOpen(false)}
          onDone={(rule) => {
            setCustomOpen(false);
            onChange(rule);
          }}
        />
      )}
    </div>
  );
};

export default RecurrenceField;
